import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import { GridCellProps } from '@progress/kendo-react-grid';
import moment from 'moment-timezone';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Button from 'src/components/design-system/Button';
import Dialog from 'src/components/design-system/Dialog';
import GridCellBase from 'src/components/GridCellBase';
import { CanClosePeriodPermissions } from 'src/constants/permissionsList';
import useBoolean from 'src/hooks/useBoolean';
import usePermission from 'src/hooks/usePermissions';
import useRedirect from 'src/hooks/useRedirect';
import useViewCalendarYear from 'src/hooks/useViewCalendarYear';
import { formatCalendarYearTable, GLPeriodCalendarYear } from 'src/utils/transformation';
const Status: React.FC<GridCellProps> = (props) => {
  const { dataItem } = props;
  const { isClosed, glPeriodId, canClosePeriod } = dataItem;
  const {
    isUpdatingCloseGLPeriod,
    updateCloseGLPeriod,
    companyId,
    getGLPeriods,
    setSelectedFYPeriod,
    glPeriods,
  } = useViewCalendarYear();
  const { value: isModalOpen, setFalse: handleCloseModal, setTrue: handleOpenModal } = useBoolean();
  const [loadingButton, setLoadingButton] = useState<string | null>(null);
  const { t } = useTranslation();
  const hasCanClosePeriodPermission = usePermission({ permissions: CanClosePeriodPermissions });
  const { getUrl, redirect } = useRedirect();

  const handleClosePeriod = useCallback(
    async (shouldNavigate = false) => {
      try {
        setLoadingButton(shouldNavigate ? 'viewAccrual' : 'closePeriod');
        await updateCloseGLPeriod({ companyId, glPeriodId });
        handleCloseModal();
        toast.success(t('Period Closed successfully'));
        getGLPeriods().then((data) => {
          setSelectedFYPeriod(
            (prev) =>
              formatCalendarYearTable(data.glPeriods).find(
                (yearRow) => prev.year === yearRow.year
              ) as GLPeriodCalendarYear
          );
        });
        if (shouldNavigate) {
          redirect(getUrl('accrual'), false, true);
        }
      } catch (error) {
        console.error('Failed to close period:', error);
      } finally {
        setLoadingButton(null);
      }
    },

    [
      companyId,
      getGLPeriods,
      getUrl,
      glPeriodId,
      handleCloseModal,
      redirect,
      setSelectedFYPeriod,
      t,
      updateCloseGLPeriod,
    ]
  );

  const periodStatus = useMemo(() => {
    const sixMonthsAgo = moment().subtract(6, 'months');
    const givenDate = new Date(dataItem?.startDate);
    return moment(givenDate).isBefore(sixMonthsAgo);
  }, [dataItem?.startDate]);

  return (
    <GridCellBase {...props} className="!text-center !p-0 relative" disableToolTip>
      <div className="flex items-center flex-row justify-center absolute inset-0">
        <span>
          {(glPeriods[0].isClosed === 0 && glPeriods[0].glPeriodId === glPeriodId) ||
          (canClosePeriod && hasCanClosePeriodPermission) ? (
            <Button size="sm" color="info" onClick={handleOpenModal}>
              Close Period
            </Button>
          ) : isClosed ? (
            'Closed'
          ) : (
            'Open'
          )}
        </span>
      </div>
      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        title={'Close Period'}
        id="modal"
        width="50%"
      >
        <div>
          Closing the period will not allow the submission or completion of any additional POs,
          invoices, or credit memos. Any in-progress documents will need to be reassigned to an open
          period in order to complete them.
        </div>
        <div className="mt-4 mb-2"> Please confirm that you wish to close this period. </div>
        <DialogActionsBar>
          <div className="flex justify-end mr-5">
            <Button
              size="md"
              color="info"
              loading={isUpdatingCloseGLPeriod && loadingButton === 'closePeriod'}
              onClick={() => handleClosePeriod(false)}
            >
              Close Period
            </Button>

            {periodStatus ? (
              ''
            ) : (
              <Button
                size="md"
                color="info"
                loading={isUpdatingCloseGLPeriod && loadingButton === 'viewAccrual'}
                onClick={() => handleClosePeriod(true)}
              >
                Close Period and View Accural
              </Button>
            )}

            <Button size="md" color="info" onClick={handleCloseModal}>
              Cancel
            </Button>
          </div>
        </DialogActionsBar>
      </Dialog>
    </GridCellBase>
  );
};

export default Status;
